// Phase NAATI-MAX B3 — /en/translate/naati/doctype/$slug per-doctype hub (EN mirror)
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { NAATI_DOCTYPES, type NaatiDoctype } from "@/data/naati-doctypes";
import { NAATI_CATEGORIES } from "@/data/naati-categories";
import { NAATI_COUNTRIES } from "@/data/naati-countries";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/en/translate/naati/doctype/$slug")({
  loader: ({ params }) => {
    const doc = NAATI_DOCTYPES.find((d) => d.slug === params.slug);
    if (!doc) throw notFound();
    const parent = NAATI_CATEGORIES.find((c) => c.doctypeSlugs.includes(doc.slug));
    const siblings = parent
      ? parent.doctypeSlugs
          .map((s) => NAATI_DOCTYPES.find((d) => d.slug === s))
          .filter((d): d is NaatiDoctype => Boolean(d) && d!.slug !== doc.slug)
      : [];
    return { doc, parent, siblings };
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) return {};
    const { doc } = loaderData;
    const url = `${SITE}/en/translate/naati/doctype/${params.slug}`;
    const thUrl = `/translate/naati/doctype/${params.slug}`;
    const title = `${doc.nameEn} NAATI Certified Translation — From THB ${doc.priceFromThb.toLocaleString()}/page | NYC`;
    const desc = `Professional NAATI-certified translation for ${doc.nameEn}. Digital QR-stamped PDF, upload to ImmiAccount instantly. Turnaround ${doc.turnaround}. DHA acceptance guaranteed. Free consultation, fast reply.`;
    return {
      meta: [
        { title: ctaTitle(title, "/en/translate/naati/doctype/$slug", "en") }, { name: "description", content: ctaDesc(desc, "/en/translate/naati/doctype/$slug", "en")},
        { property: "og:title", content: ctaTitle(title, "/en/translate/naati/doctype/$slug", "en")}, { property: "og:url", content: url },
        { property: "og:description", content: ctaDesc(desc, "/en/translate/naati/doctype/$slug", "en")}, { property: "og:locale", content: "en_US" },
      ],
      links: canonicalAndHreflang(`/en/translate/naati/doctype/${params.slug}`, thUrl),
      scripts: [
        { type: "application/ld+json", children: JSON.stringify({
          "@context": "https://schema.org", "@type": "Service",
          "@id": `${url}#service`, name: title, description: desc, inLanguage: "en",
          serviceType: "NAATI Certified Translation",
          provider: { "@type": "ProfessionalService", name: "NYC Language Institute", url: SITE },
          areaServed: { "@type": "Country", name: "Australia" },
          offers: { "@type": "Offer", price: doc.priceFromThb, priceCurrency: "THB",
            availability: "https://schema.org/InStock",
            priceSpecification: { "@type": "UnitPriceSpecification", price: doc.priceFromThb, priceCurrency: "THB", unitText: "per A4 page" } },
        })},
        { type: "application/ld+json", children: JSON.stringify({
          "@context": "https://schema.org", "@type": "FAQPage",
          mainEntity: [
            { "@type": "Question", name: `How much is NAATI translation of ${doc.nameEn}?`,
              acceptedAnswer: { "@type": "Answer", text: `From THB ${doc.priceFromThb.toLocaleString()} per A4 page, including the NAATI Digital Stamp with QR Code. Turnaround ${doc.turnaround}. PDF delivered via email, ready to upload to ImmiAccount.`}},
            { "@type": "Question", name: `What do I need to provide?`,
              acceptedAnswer: { "@type": "Answer", text: doc.notesEn }},
            { "@type": "Question", name: `Which Australian visa subclasses accept this?`,
              acceptedAnswer: { "@type": "Answer", text: `Used for ${doc.useCases.join(", ")}, and any DHA visa requiring certified translation.`}},
            { "@type": "Question", name: `Can the NAATI Digital Stamp be forged?`,
              acceptedAnswer: { "@type": "Answer", text: `No — the NAATI digital stamp embeds a QR code linked to NAATI's database. Anyone can scan to verify the translator's CPN (renewed every 3 years) and the translation's integrity.`}},
          ],
        })},
        { type: "application/ld+json", children: JSON.stringify({
          "@context": "https://schema.org", "@type": "HowTo",
          name: `How to order NAATI certified translation of ${doc.nameEn}`,
          totalTime: `PT${doc.turnaround.match(/\d+/)?.[0] ?? "3"}D`,
          estimatedCost: { "@type": "MonetaryAmount", currency: "THB", value: doc.priceFromThb },
          step: [
            { "@type": "HowToStep", position: 1, name: "Send document", text: `Scan or photograph the ${doc.nameEn} at 300dpi+ and send via LINE or email.` },
            { "@type": "HowToStep", position: 2, name: "Price confirmation", text: `We review within 30 minutes and issue a quote with a job number.` },
            { "@type": "HowToStep", position: 3, name: "Payment", text: `Bank transfer or credit card; tax invoice on request.` },
            { "@type": "HowToStep", position: 4, name: "Delivery", text: `Within ${doc.turnaround}: PDF with NAATI QR digital stamp, upload to ImmiAccount immediately.` },
          ],
        })},
        { type: "application/ld+json", children: JSON.stringify({
          "@context": "https://schema.org", "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/en` },
            { "@type": "ListItem", position: 2, name: "NAATI", item: `${SITE}/en/translate/naati` },
            { "@type": "ListItem", position: 3, name: doc.nameEn, item: url },
          ],
        })},
        { type: "application/ld+json", children: JSON.stringify({
          "@context": "https://schema.org", "@type": "WebPage", "@id": `${url}#speakable`, url, name: "NAATI Certified Translation",
          speakable: { "@type": "SpeakableSpecification", cssSelector: ["[data-speakable]", "h1", ".direct-answer"] },
        })},
      ],
    };
  },
  component: DoctypePageEn,
});

function DoctypePageEn() {
  const { doc, parent, siblings } = Route.useLoaderData() as {
    doc: NaatiDoctype;
    parent: typeof NAATI_CATEGORIES[number] | undefined;
    siblings: NaatiDoctype[];
  };
  return (
    <main className="mx-auto max-w-5xl px-5 py-12 sm:px-8 sm:py-16">
      <nav className="mb-6 text-xs text-slate-500">
        <Link to="/en" className="hover:text-amber-700">Home</Link> /{" "}
        <Link to="/en/translate/naati" className="hover:text-amber-700">NAATI</Link> /{" "}
        <span>{doc.nameEn}</span>
      </nav>
      <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">NAATI Certified · {doc.category.toUpperCase()}</div>
      <h1 className="mt-2 font-display text-3xl font-extrabold text-navy-deep sm:text-5xl" data-speakable="true">
        {doc.nameEn} — NAATI Certified Translation
      </h1>
      <p className="mt-3 text-lg text-slate-700 direct-answer" data-speakable="true">
        From <strong>THB {doc.priceFromThb.toLocaleString()}/page</strong>, including the NAATI Digital QR Stamp.
        Turnaround <strong>{doc.turnaround}</strong>. PDF delivered by email — upload to ImmiAccount immediately.
        No postage required. DHA acceptance guaranteed.
      </p>

      <section className="mt-10 grid gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">Price</div>
          <div className="mt-1 text-2xl font-extrabold text-navy-deep">THB {doc.priceFromThb.toLocaleString()}</div>
          <div className="text-xs text-slate-500">per A4 page (NAATI stamp included)</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">Turnaround</div>
          <div className="mt-1 text-2xl font-extrabold text-navy-deep">{doc.turnaround}</div>
          <div className="text-xs text-slate-500">Express +50% within 24h</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">Typical length</div>
          <div className="mt-1 text-2xl font-extrabold text-navy-deep">{doc.pagesTypical}</div>
          <div className="text-xs text-slate-500">Priced per actual page count</div>
        </div>
      </section>

      <section className="mt-10">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">Requirements & what to prepare</h2>
        <p className="mt-3 text-base leading-relaxed text-slate-700">{doc.notesEn}</p>
      </section>

      <section className="mt-10">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">Australian visas / authorities that accept this document</h2>
        <ul className="mt-3 grid gap-2 sm:grid-cols-2">
          {doc.useCases.map((u, i) => (
            <li key={i} className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700">{u}</li>
          ))}
        </ul>
      </section>

      <section className="mt-10 rounded-2xl border border-amber-300 bg-gradient-to-br from-amber-50 to-white p-6">
        <h2 className="font-display text-xl font-extrabold text-navy-deep">4-step ordering process</h2>
        <ol className="mt-3 space-y-2 text-sm text-slate-700">
          <li><strong>1) Send document:</strong> Scan/photo at 300dpi+ via LINE @nyclanguage or email.</li>
          <li><strong>2) Quote:</strong> 30-min review, quote and job number issued.</li>
          <li><strong>3) Payment:</strong> Bank transfer or credit card (tax invoice available).</li>
          <li><strong>4) Delivery:</strong> Within {doc.turnaround}: PDF with NAATI QR digital stamp, upload to ImmiAccount.</li>
        </ol>
      </section>

      <section className="mt-10">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">FAQ</h2>
        <div className="mt-4 space-y-4">
          <details className="rounded-xl border border-slate-200 bg-white p-4" open>
            <summary className="cursor-pointer font-semibold text-navy-deep">Can the NAATI Digital Stamp be forged?</summary>
            <p className="mt-2 text-sm text-slate-700">No — the digital stamp embeds a QR code linked to NAATI's database. Anyone can verify the translator's CPN (renewed every 3 years) and the translation's integrity.</p>
          </details>
          <details className="rounded-xl border border-slate-200 bg-white p-4">
            <summary className="cursor-pointer font-semibold text-navy-deep">Will DHA reject our translation?</summary>
            <p className="mt-2 text-sm text-slate-700">No — our translators hold an active NAATI CPN and follow the NAATI Code of Ethics. If revisions are requested, we revise free for the document's lifetime.</p>
          </details>
          <details className="rounded-xl border border-slate-200 bg-white p-4">
            <summary className="cursor-pointer font-semibold text-navy-deep">Is 24-hour express delivery possible?</summary>
            <p className="mt-2 text-sm text-slate-700">Yes — express surcharge +50%. Submit before 11:00 AM ICT for delivery by 18:00 ICT the next business day.</p>
          </details>
        </div>
      </section>

      {siblings.length > 0 && (
        <section className="mt-10">
          <h2 className="font-display text-xl font-extrabold text-navy-deep">Related documents{parent ? ` (${parent.nameEn})` : ""}</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {siblings.slice(0, 9).map((s) => (
              <Link key={s.slug} to="/en/translate/naati/doctype/$slug" params={{ slug: s.slug }}
                className="rounded-xl border border-slate-200 bg-white p-4 hover:border-amber-400">
                <div className="font-semibold text-navy-deep">{s.nameEn}</div>
                <div className="text-xs text-slate-500">THB {s.priceFromThb.toLocaleString()} · {s.turnaround}</div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="mt-10 rounded-2xl border border-slate-200 bg-slate-50 p-6">
        <h2 className="font-display text-xl font-extrabold text-navy-deep">{doc.nameEn} translation by destination</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {NAATI_COUNTRIES.slice(0, 12).map((c) => (
            <Link key={c.slug} to="/en/translate/naati/online/$country" params={{ country: c.slug }}
              className="rounded-full border border-slate-300 bg-white px-3 py-1 text-xs hover:border-amber-400">
              {c.nameEn}
            </Link>
          ))}
        </div>
      </section>
      <QuotableStatsStrip cluster="naati" locale="en" variant="light" />
    </main>
  );
}
